import { useCallback, useEffect, useRef, useState } from 'react';
import type { Npc } from '../../types.js';
import type { AbsenceReason } from '../../runtime/characterAbsence.js';
import {
  createAbsenceRecord,
  isAbsenceActive,
  undoAbsence,
} from '../../runtime/characterAbsence.js';
import {
  closedEpisodeMessage,
  isLocationOpen,
  recordCharacterAbsence,
} from '../worldRuntime/adapter.js';
import { worldTimezone } from '../worldRuntime/clock.js';
import { saveRuntimeState } from '../worldRuntime/persistence.js';
import type { AquariumRuntimeView, PersistedRuntimeState, RuntimeMenuPanel } from '../worldRuntime/types.js';
import CatchUpCover from './CatchUpCover.js';
import EventStorySummary from './EventStorySummary.js';
import { chromeStyles as s } from './aquariumStyles.js';
import HistoryTreeView from './HistoryTreeView.js';
import { useHistoryPanelOpen } from './useHistoryPanelOpen.js';

export default function AquariumChrome(props: {
  view: AquariumRuntimeView;
  npcs: Npc[];
  runtimeState?: PersistedRuntimeState | null;
  setRuntimeState?: React.Dispatch<React.SetStateAction<PersistedRuntimeState | null>>;
  onFocusCharacter: (id: string) => void;
  onStopFocus?: () => void;
  onDismissMissed: () => void;
  onBackToMenu?: () => void;
  onAddCharacter?: (npc: Npc) => void;
  onRemoveCharacter?: (id: string) => void;
  onReturnToCharacterCreation?: () => void;
  characterActivity?: (id: string) => string;
}): JSX.Element {
  const {
    view,
    npcs,
    runtimeState,
    setRuntimeState,
    onFocusCharacter,
    onStopFocus,
    onDismissMissed,
    onBackToMenu,
    onAddCharacter,
    onRemoveCharacter,
    onReturnToCharacterCreation,
    characterActivity,
  } = props;
  const [menuOpen, setMenuOpen] = useState(false);
  const [panel, setPanel] = useState<RuntimeMenuPanel>(null);
  const [historyCharId, setHistoryCharId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Npc | null>(null);
  const [focusPickerOpen, setFocusPickerOpen] = useState(false);
  const wasCatchingUp = useRef(view.catchingUp);

  useEffect(() => {
    const was = wasCatchingUp.current;
    wasCatchingUp.current = view.catchingUp;
    if (was && !view.catchingUp && view.showMissedPrompt) setPanel('missed');
  }, [view.catchingUp, view.showMissedPrompt]);

  const open = isLocationOpen(view.operatingStatus);
  const focusTarget = view.focusedCharacterId;
  const focusNpc = focusTarget ? npcs.find((n) => n.id === focusTarget) : null;
  const title = view.locationLabel;

  const closeMenu = useCallback((): void => {
    setMenuOpen(false);
    setFocusPickerOpen(false);
  }, []);

  const {
    dayStory,
    openLocationHistory,
    openCharacterHistoryPick,
    openCharacterHistory,
    cancelPending,
    clearDayStories,
  } = useHistoryPanelOpen({
    view,
    setPanel,
    setHistoryCharId,
    closeMenu,
  });

  const showMissedMenuItem =
    view.missedEvents.length > 0 || Boolean(view.missedEventStory?.trim());

  const openPanel = useCallback((next: RuntimeMenuPanel): void => {
    cancelPending();
    setPanel(next);
    closeMenu();
  }, [cancelPending, closeMenu]);

  const toggleFullscreen = useCallback((): void => {
    const el = document.documentElement;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void el.requestFullscreen?.();
    }
  }, []);

  const foreverRemoved = view.characterAbsences.filter(
    (r) => r.reason === 'forever' && !r.undoneAt && isAbsenceActive(r, view.clock.inWorldIso),
  );

  function handleRemove(reason: AbsenceReason): void {
    if (!removeTarget || !runtimeState || !setRuntimeState) return;
    if (reason === 'forever' && !window.confirm(`Remove ${removeTarget.firstName} forever?`)) return;
    const record = createAbsenceRecord({
      characterId: removeTarget.id,
      firstName: removeTarget.firstName,
      reason,
      removedAt: runtimeState.clock.inWorldIso,
    });
    setRuntimeState(recordCharacterAbsence(runtimeState, record));
    onRemoveCharacter?.(removeTarget.id);
    setRemoveTarget(null);
    setPanel(null);
  }

  function handleUndoForever(characterId: string): void {
    if (!runtimeState || !setRuntimeState) return;
    const next = {
      ...runtimeState,
      characterAbsences: undoAbsence(
        runtimeState.characterAbsences ?? [],
        characterId,
        runtimeState.clock.inWorldIso,
      ),
    };
    saveRuntimeState(next);
    setRuntimeState(next);
    const rosterNpc = view.handoff.roster.find((n) => n.id === characterId);
    if (rosterNpc) onAddCharacter?.({ ...rosterNpc });
  }

  function renderPanel(): JSX.Element | null {
    if (!panel) return null;
    const close = (): void => {
      setPanel(null);
      setHistoryCharId(null);
      setRemoveTarget(null);
      clearDayStories();
    };

    return (
      <div style={s.panelBack} onClick={close} role="presentation">
        <div
          style={s.panelFull}
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
        >
          <header style={s.panelHeader}>
            <h2 style={{ margin: 0, fontSize: 18 }}>
              {panel === 'location-summary' && 'Location summary'}
              {panel === 'location-history' && 'Location history'}
              {panel === 'character-history-pick' && 'Character history'}
              {panel === 'character-history' && 'Character history'}
              {panel === 'remove-character' && 'Remove character'}
              {panel === 'missed' && 'What you missed'}
            </h2>
            <button type="button" className="btn-ghost" onClick={close}>Close</button>
          </header>
          <div style={s.panelBody}>
            {panel === 'location-summary' && (
              <>
                <p>Location: {view.snapshot.location.region}</p>
                <p>Timezone: {worldTimezone(view.snapshot.schedule, view.snapshot.location.region)}</p>
                <p>{view.snapshot.connected.description}</p>
                {view.locationSummary?.observedFacts && <p>{view.locationSummary.observedFacts}</p>}
              </>
            )}
            {panel === 'location-history' && (
              <>
                {dayStory && <EventStorySummary story={dayStory} />}
                <HistoryTreeView events={view.historyEvents} />
              </>
            )}
            {panel === 'character-history-pick' && (
              <>
                {dayStory && <EventStorySummary story={dayStory} />}
                <ul style={s.pickerList}>
                  {npcs.map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        style={s.pickerItem}
                        onClick={() => openCharacterHistory(n.id)}
                      >
                        {n.firstName}
                        {characterActivity?.(n.id) ? ` — ${characterActivity(n.id)}` : ''}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {panel === 'character-history' && historyCharId && (() => {
              const n = npcs.find((x) => x.id === historyCharId);
              return (
                <>
                  <button type="button" className="btn-ghost" onClick={() => setPanel('character-history-pick')}>
                    ← All characters
                  </button>
                  <h3>{n?.firstName ?? 'Character'}</h3>
                  {dayStory && <EventStorySummary story={dayStory} />}
                  <HistoryTreeView
                    events={view.historyEvents.filter((event) => event.actorIds.includes(historyCharId))}
                  />
                </>
              );
            })()}
            {panel === 'remove-character' && !removeTarget && (
              <>
                <ul style={s.pickerList}>
                  {npcs.map((n) => (
                    <li key={n.id}>
                      <button type="button" style={s.pickerItem} onClick={() => setRemoveTarget(n)}>
                        {n.firstName}
                      </button>
                    </li>
                  ))}
                </ul>
                {foreverRemoved.length > 0 && (
                  <>
                    <h3 style={{ marginTop: 20, fontSize: 16 }}>Removed (undoable, 6 months)</h3>
                    <ul style={s.pickerList}>
                      {foreverRemoved.map((r) => (
                        <li key={r.characterId} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span>{r.firstName}</span>
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={() => handleUndoForever(r.characterId)}
                          >
                            Undo / Restore
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
            {panel === 'remove-character' && removeTarget && (
              <>
                <p>Remove {removeTarget.firstName}:</p>
                <div className="menu-row">
                  <button type="button" className="btn-primary" onClick={() => handleRemove('holiday')}>
                    Holiday (2 weeks, auto-return)
                  </button>
                  <button type="button" className="btn-primary" onClick={() => handleRemove('lunch')}>
                    Lunch (1 hour, auto-return)
                  </button>
                  <button type="button" className="btn-ghost" onClick={() => handleRemove('forever')}>
                    Remove forever (undoable, 6mo list)
                  </button>
                  <button type="button" className="btn-ghost" onClick={() => setRemoveTarget(null)}>Back</button>
                </div>
              </>
            )}
            {panel === 'missed' && (
              <>
                {view.missedEvents.length > 0 ? (
                  <>
                    {view.missedEventStory && <EventStorySummary story={view.missedEventStory} />}
                    <HistoryTreeView events={view.missedEvents} collapseAll />
                  </>
                ) : (
                  <p>No unobserved activity was recorded while you were away.</p>
                )}
                <button type="button" className="btn-ghost" onClick={() => { onDismissMissed(); close(); }}>
                  Dismiss
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <header className="aquarium-header">
        <button
          type="button"
          style={s.fullscreenBtn}
          aria-label="Toggle fullscreen"
          title="Fullscreen"
          onClick={toggleFullscreen}
        >
          ⛶
        </button>

        <div className="aquarium-title-layer">
          <h1 className="aquarium-title">{title}</h1>
          <p className="aquarium-context">{view.subtitle}</p>
        </div>

        <div className="aquarium-bar-actions">
          <button
            type="button"
            className="aquarium-focus-btn"
            aria-expanded={focusPickerOpen}
            onClick={() => setFocusPickerOpen((v) => !v)}
          >
            Focus on: {focusNpc ? focusNpc.firstName : '—'}
          </button>
          <div className="aquarium-menu-wrap">
            <button
              type="button"
              className="aquarium-menu-btn"
              aria-label="Location and history menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              ⋯
            </button>
            {menuOpen && (
              <div className="aquarium-menu-panel" role="menu">
                <button type="button" role="menuitem" onClick={() => openPanel('location-summary')}>
                  Location summary
                </button>
                <button type="button" role="menuitem" onClick={() => { void openLocationHistory(); }}>
                  Location history
                </button>
                <button type="button" role="menuitem" onClick={() => { void openCharacterHistoryPick(); }}>
                  Character history
                </button>
                {showMissedMenuItem && (
                  <button type="button" role="menuitem" onClick={() => openPanel('missed')}>
                    What you missed
                  </button>
                )}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); onReturnToCharacterCreation?.(); }}
                >
                  Add character
                </button>
                <button type="button" role="menuitem" onClick={() => openPanel('remove-character')}>
                  Remove character
                </button>
                {onBackToMenu && (
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onBackToMenu(); }}>
                    Back to main menu
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {focusPickerOpen && (
        <div style={s.panelBack} onClick={() => setFocusPickerOpen(false)} role="presentation">
          <div style={{ ...s.panelFull, maxHeight: '60vh', height: 'auto', margin: 'auto', width: 'min(92vw, 400px)' }} onClick={(e) => e.stopPropagation()}>
            <header style={s.panelHeader}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Change focus</h2>
              <button type="button" className="btn-ghost" onClick={() => setFocusPickerOpen(false)}>Close</button>
            </header>
            <div style={s.panelBody}>
              <ul style={s.pickerList}>
                <li>
                  <button
                    type="button"
                    style={s.pickerItem}
                    onClick={() => {
                      onStopFocus?.();
                      setFocusPickerOpen(false);
                    }}
                  >
                    Stop focusing
                  </button>
                </li>
                {npcs.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      style={s.pickerItem}
                      onClick={() => {
                        onFocusCharacter(n.id);
                        setFocusPickerOpen(false);
                      }}
                    >
                      {n.firstName}
                      {characterActivity?.(n.id) ? ` — ${characterActivity(n.id)}` : ''}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {!open && (
        <div style={s.closedBanner} role="status">
          {closedEpisodeMessage()}
        </div>
      )}

      {view.isEmptyRoster && (
        <div className="aquarium-empty-roster" role="status">
          No characters in this world yet. Return to Create World to add inhabitants.
        </div>
      )}

      {renderPanel()}
      {view.catchingUp && <CatchUpCover />}
    </>
  );
}
